import React from 'react';
import { CATEGORY_KEYS, stripCategoryPrefix } from '../utils/categoryKeywords';
import '../styles/CategoryIcon.css';

// Import individual category icons
import workIcon from '../assets/images/work.png';
import devIcon from '../assets/images/dev.png';
import aiIcon from '../assets/images/KI.png';
import designIcon from '../assets/images/design.png';
import toolsIcon from '../assets/images/tools.png';
import wissenIcon from '../assets/images/wissen.png';
import shoppingIcon from '../assets/images/shopping.png';
import dealsIcon from '../assets/images/deals.png';
import financeIcon from '../assets/images/finance.png';
import foodIcon from '../assets/images/food.png';
import entertainmentIcon from '../assets/images/entertainment.png';
import gamingIcon from '../assets/images/gaming.png';
import diyIcon from '../assets/images/diy.png';
import mustReadIcon from '../assets/images/must-read.png';
import laterIcon from '../assets/images/later.png';
import sonstigesIcon from '../assets/images/sonstiges.png';
import holidayIcon from '../assets/images/holiday.png';
import sportIcon from '../assets/images/sport.png';
import snacksIcon from '../assets/images/snacks.png';
import mainIcon from '../assets/images/main.png';
import bakingIcon from '../assets/images/backwaren.png';
import dessertIcon from '../assets/images/dessert.png';

/**
 * Mapping of category keys to individual icon images
 */
const CATEGORY_ICON_MAP = {
  [CATEGORY_KEYS.WORK]: workIcon,
  [CATEGORY_KEYS.DEV]: devIcon,
  [CATEGORY_KEYS.AI]: aiIcon,
  [CATEGORY_KEYS.DESIGN]: designIcon,
  [CATEGORY_KEYS.TOOLS]: toolsIcon,
  [CATEGORY_KEYS.NEWS]: wissenIcon,
  [CATEGORY_KEYS.SHOPPING]: shoppingIcon,
  [CATEGORY_KEYS.DEALS]: dealsIcon,
  [CATEGORY_KEYS.FINANCE]: financeIcon,
  [CATEGORY_KEYS.FOOD]: foodIcon,
  [CATEGORY_KEYS.ENTERTAINMENT]: entertainmentIcon,
  [CATEGORY_KEYS.GAMING]: gamingIcon,
  [CATEGORY_KEYS.DIY]: diyIcon,
  [CATEGORY_KEYS.IMPORTANT]: mustReadIcon,
  [CATEGORY_KEYS.INBOX]: laterIcon,
  [CATEGORY_KEYS.RANDOM]: sonstigesIcon,
  [CATEGORY_KEYS.OTHER]: sonstigesIcon,
  [CATEGORY_KEYS.TRAVEL]: holidayIcon,
  [CATEGORY_KEYS.FITNESS]: sportIcon,
  [CATEGORY_KEYS.SNACKS]: snacksIcon,
  [CATEGORY_KEYS.MEALS]: mainIcon,
  [CATEGORY_KEYS.BAKING]: bakingIcon,
  [CATEGORY_KEYS.DESSERT]: dessertIcon,
};

// Default icon for unmapped categories
const DEFAULT_ICON = sonstigesIcon;

/**
 * CategoryIcon Component
 *
 * Displays a category icon using individual image files
 * Icons are centered and scaled to fit the container.
 *
 * @param {string} category - Category key (e.g., "DEV", "AI", "WORK")
 * @param {number} size - Icon container size in pixels (default: 52)
 * @param {string} className - Additional CSS class
 */
const CategoryIcon = ({ category, size = 52, className = '' }) => {
  const iconSrc = CATEGORY_ICON_MAP[category] || DEFAULT_ICON;

  const containerStyle = {
    width: `${size}px`,
    height: `${size}px`,
  };

  return (
    <div
      className={`category-icon-container ${className}`}
      style={containerStyle}
      role="img"
      aria-label={`${category} icon`}
      data-category={category}
    >
      <img
        src={iconSrc}
        alt={`${category} category`}
        className="category-icon-image"
      />
    </div>
  );
};

/**
 * Get category display name for i18n
 * Handles legacy "categories." prefix and returns translated name
 */
export const getCategoryDisplayName = (category, t) => {
  // Strip "categories." prefix if present (legacy data format)
  const cleanCategory = stripCategoryPrefix(category);
  const key = `categories.${cleanCategory?.toLowerCase() || 'other'}`;
  return t(key) || cleanCategory || 'Other';
};

export default CategoryIcon;
